import React, { useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { joinWaitlistApi } from "../../api/api";
import { WINDOW_OPTIONS, joinErrorMessage } from "./waitlistModel";
import "./listaDeEspera.css";

/**
 * "Avisarme si se libera": link + formulario para anotarse en la lista de espera del local.
 * No decide nada del negocio: recibe el local, el día y los criterios ya elegidos en la reserva
 * (servicios, deporte y duración, personas) y los manda tal cual. Si el local no la activó, no pinta nada.
 * `kind`: "beauty" | "court" | "table". `summary`: texto corto de lo que se pidió (opcional).
 */
const WEEKDAYS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];

/** "jueves 29/10": más amable que DD/MM/YYYY para el aviso. */
const friendlyDay = (date) => {
  const d = dayjs(date);
  return d.isValid() ? `${WEEKDAYS[d.day()]} ${d.format("DD/MM")}` : String(date);
};

const WaitlistJoin = ({ enabled, barId, kind, date, criteria, summary = "", label = "Avisarme si se libera" }) => {
  const token = useSelector((state) => state.auth?.accessToken);
  const user = useSelector((state) => state.auth?.user) || {};
  const [open, setOpen] = useState(false);
  const [timeWindow, setTimeWindow] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  if (!enabled || !barId || !date || !criteria) return null;

  const show = () => {
    // Con cuenta, el contacto sale del perfil; lo que el usuario ya escribió no se pisa.
    setName((v) => v || user?.name || "");
    setEmail((v) => v || user?.email || "");
    setPhone((v) => v || user?.phone || "");
    setError("");
    setOpen(true);
  };

  const valid = name.trim().length > 0 && (phone.trim().length > 0 || email.trim().length > 0);

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || sending) return;
    setSending(true);
    setError("");
    try {
      const body = {
        barId,
        kind,
        date,
        criteria,
        contact: { name: name.trim(), phone: phone.trim(), email: email.trim() },
      };
      if (timeWindow) body.timeWindow = timeWindow;
      await joinWaitlistApi(body, token);
      setDone(true);
    } catch (err) {
      setError(joinErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    setOpen(false);
    if (done) setDone(false);
  };

  return (
    <>
      <div className="wl-cta">
        <button type="button" className="wl-link" onClick={show}>
          {label}
        </button>
      </div>
      <Modal show={open} onHide={close} centered className="wl-modal">
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="wl-title">Lista de espera</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {done ? (
            <div className="wl-done" role="status">
              <span className="wl-done-icon" aria-hidden="true">✓</span>
              <h3 className="wl-done-title">¡Listo, estás anotado!</h3>
              <p>Te avisamos si se libera un lugar ese día.</p>
              <button type="button" className="r-btn-primary wl-submit" onClick={close}>Listo</button>
            </div>
          ) : (
            <form onSubmit={submit} className="wl-form">
              <p className="wl-lead">Si se libera un horario, te avisamos para que lo reserves.</p>
              <div className="wl-summary">
                <span className="wl-chip wl-chip-day">{friendlyDay(date)}</span>
                {summary && <span className="wl-chip">{summary}</span>}
              </div>
              <div className="wl-field">
                <label className="wl-label" htmlFor="wl-window">Franja (opcional)</label>
                <select id="wl-window" className="wl-input" value={timeWindow} disabled={sending} onChange={(e) => setTimeWindow(e.target.value)}>
                  {WINDOW_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="wl-field">
                <label className="wl-label" htmlFor="wl-name">Nombre</label>
                <input id="wl-name" className="wl-input" value={name} disabled={sending} onChange={(e) => setName(e.target.value)} autoComplete="name" />
              </div>
              <div className="wl-field">
                <label className="wl-label" htmlFor="wl-phone">Teléfono (WhatsApp)</label>
                <input id="wl-phone" type="tel" className="wl-input" value={phone} disabled={sending} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" placeholder="+54 9 11 2345 6789" />
              </div>
              <div className="wl-field">
                <label className="wl-label" htmlFor="wl-email">Email</label>
                <input id="wl-email" type="email" className="wl-input" value={email} disabled={sending} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
              </div>
              <p className="wl-hint">Dejanos al menos un teléfono o un email para avisarte.</p>
              {error && <div className="wl-error" role="alert">{error}</div>}
              <button type="submit" className="r-btn-primary wl-submit" disabled={!valid || sending}>
                {sending ? <Spinner animation="border" size="sm" /> : "Anotarme"}
              </button>
            </form>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default WaitlistJoin;
